import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowBottom } from "@coreui/icons";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { createBulkStore } from "src/services/store.services";
import toast from "react-hot-toast";
import { getActiveCountryList } from "src/services/country.services";

const schema = z.object({
  stores: z.array(z.any()).min(1, "Please upload your store information"),
});

const BulkStoreForm = () => {
  const ref = useRef();
  const [userInfo, setUserInfo] = useState(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );
  const [countryData, setCountryData] = useState([]);
  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    '"Something went wrong. Please try again"'
  );

  useEffect(() => {
    if (userInfo?.userType === "superAdmin") {
      getActiveCountry();
    }
  }, []);

  const getActiveCountry = () => {
    const limit = 100;
    const page = 1;
    getActiveCountryList({ limit, page, status: true })
      .then(async (response) => {
        const countryData = response.data;
        setCountryData(countryData);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      stores: [],
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data) => {
    console.log(data.stores.length);
    toast
      .promise(
        createBulkStore(data),
        {
          loading: "Loading",
          success: "Location Created Succesfully",
          error: (err) => {
            return err.response &&
              err.response.data &&
              err.response.data.message
              ? err.response.data.message
              : defaultErrorMessage;
          },
        },
        {
          id: "store",
        }
      )
      .then(async (response) => {
        reset();
        ref.current.value = "";
      })
      .catch((error) => console.error(error));
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const convertedData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        });
        let serialArray = [];
        let pushData = false;

        let breakLoop = false;

        for (let rowIndex = 0; rowIndex < convertedData.length; rowIndex++) {
          const row = convertedData[rowIndex];
          const headerNames = convertedData[0];
          const rowData = {};

          for (let i = 0; i < headerNames.length; i++) {
            const headerName = new String(headerNames[i]).trim();
            const value = row[i];

            if (value && headerName == "country" && value != "country") {
              if (userInfo.userType === "superAdmin") {
                const result = countryData.filter((obj) => {
                  return obj.code.toLowerCase() === value.toLowerCase();
                });
                if (result.length > 0) {
                  rowData[headerName] = result[0]._id;
                } else {
                  rowData[headerName] = "";
                }
              } else {
                if (value === userInfo.country.code) {
                  rowData[headerName] = userInfo.country._id;
                } else {
                  breakLoop = true;
                  break;
                }
              }
            } else {
              rowData[headerName] = value;
            }
          }

          if (breakLoop) {
            alert("Your file contains different country data");
            break;
          }

          if (pushData && !breakLoop) {
            serialArray.push(rowData);
          }
          pushData = true;
        }

        // convertedData.forEach((row) => {
        //   // Access the header names and values
        //   const headerNames = convertedData[0];
        //   const rowData = {};

        //   for (let i = 0; i < headerNames.length; i++) {
        //     const headerName = new String(headerNames[i]).trim();
        //     const value = row[i];
        //     if (value && headerName == "country" && value != "country") {
        //       if (userInfo.userType === "superAdmin") {
        //         const result = countryData.filter((obj) => {
        //           return obj.code.toLowerCase() === value.toLowerCase();
        //         });
        //         if (result.length > 0) {
        //           rowData[headerName] = result[0]._id;
        //         } else {
        //           rowData[headerName] = "";
        //         }
        //       } else {
        //         if (value === userInfo.country.code) {
        //           rowData[headerName] = userInfo.country._id;
        //           console.log(rowData);
        //         } else {
        //           alert("Your file contains different country data");
        //           break;
        //         }
        //       }
        //     } else {
        //       rowData[headerName] = value;
        //     }
        //   }

        //   if (pushData) {
        //     serialArray.push(rowData);
        //   }
        //   pushData = true;
        // });
        // const convertedData = XLSX.utils.sheet_to_json(worksheet, {
        //   header: 1,
        //   range: 1,
        // });
        if (!breakLoop) {
          setValue("stores", serialArray);
        }
      };

      reader.readAsArrayBuffer(file);
    } else {
      setValue("stores", []);
    }
  };

  const exportToXLSX = () => {
    // Prepare your data to be exported as an array of arrays
    const data = [
      [
        "locationCode",
        "name",
        "latitude",
        "longitude",
        "address",
        "city",
        "state",
        "country",
        "zipCode",
        "contactName",
        "contactEmail",
        "contactPhone",
        "contactAddress",
      ],
      // Add more rows as needed
    ];

    // Create a new workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet 1");

    // Generate the XLSX file buffer
    const xlsxBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Create a Blob from the buffer
    const blob = new Blob([xlsxBuffer], { type: "application/octet-stream" });

    // Save the file using FileSaver.js
    saveAs(blob, "Location-Upload.xlsx");
  };

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              Upload Location Data
            </h4>
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CForm onSubmit={handleSubmit(onSubmit, onError)}>
          <CRow>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="stores"
                  className="col-sm-3 col-form-label"
                >
                  File:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileUpload}
                    id="stores"
                    ref={ref}
                  />
                  {errors.stores?.message && (
                    <p className="error_text">{errors.stores?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow>
                <div className="d-flex justify-content-center mt-5">
                  <CButton type="submit" color="primary" className="px-5 py-2">
                    Submit
                  </CButton>
                </div>
              </CRow>
            </CCol>
            <CCol sm={2}></CCol>
            <CCol sm={3}>
              <CCardBody>
                Please click on the download button and copy your store data
                according to the file structure.
                <p style={{ color: "#ff0000a3" }}>
                  S.N: In country column please write the active country code of
                  the system. Otherwise country data will not be save.
                </p>
              </CCardBody>

              <CRow className="mb-3">
                <CButton
                  color="secondary"
                  download
                  style={{ color: "white" }}
                  onClick={exportToXLSX}
                >
                  {/* <CIcon className="my-4 text-white" icon={cilArrowBottom} height={10} /> */}
                  Download Sample
                </CButton>
              </CRow>
            </CCol>
          </CRow>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default BulkStoreForm;
